import { Seat } from './Seat'
import React, { useCallback, useMemo, useState } from 'react'
import { mapStateManager } from '../MapStateManager'

const AccessibleIcon = () => (
  <defs>
    <symbol id="admin-seatmap-icon-accessible" viewBox="0 0 20 20">
      <path
        d="M7.54081 3.64526C8.50281 3.64526 9.27681 2.86326 9.27681 1.92127C9.27681 0.965266 8.50281 0.197266 7.54081 0.197266C6.58281 0.197266 5.80481 0.965266 5.80481 1.92127C5.80481 2.86326 6.58281 3.64526 7.54081 3.64526ZM17.7288 15.4393L14.7688 10.3653C14.5848 10.0593 14.2628 9.88726 13.9288 9.87726H9.58481L9.54081 8.35126H12.6948C13.1248 8.32526 13.4788 8.00126 13.4788 7.56326C13.4788 7.13126 13.1348 6.79526 12.6948 6.78126H9.45281L9.34081 5.52526C9.27681 4.62126 8.49281 3.90926 7.57281 3.95926C6.64481 4.01126 5.94481 4.80326 5.98881 5.71926L6.29481 10.9513C6.37081 11.8693 7.14281 12.5193 8.05881 12.5193H13.6888L15.9888 16.4733C16.2648 16.9213 16.9028 17.1053 17.3828 16.8193C17.8568 16.5313 17.9988 15.9393 17.7288 15.4393ZM8.65681 18.4413C5.80881 18.4413 3.49881 16.1533 3.49881 13.3213C3.49881 11.7713 4.20681 10.3873 5.30881 9.44526L5.21281 7.84526C3.36881 8.98926 2.13281 10.9993 2.13281 13.3213C2.13281 16.8973 5.05081 19.7993 8.65681 19.7993C11.3108 19.7993 13.5788 18.2133 14.5988 15.9573L13.6828 14.3773C13.1908 16.6913 11.1348 18.4413 8.65681 18.4413Z"
        fill="white"
      />
    </symbol>
  </defs>
)

const Seats = React.memo(
  ({
    data,
    isDisabled,
    selectRegularSeats,
    activeMapAction,
    getSeatState,
    getSeatTooltipDetails,
    onPurchaserClusterHover,
    showTooltip,
    hideTooltip,
    useFrontendSeatSizing = false,
    seatScale,
    seatBorderRadius,
    hiddenSectionIds = [],
  }) => {
    const [hoveredRowId, setHoveredRowId] = useState(null)
    const hiddenIds = useMemo(
      () => new Set((hiddenSectionIds || []).map((id) => String(id))),
      [hiddenSectionIds]
    )
    const rows = useMemo(() => {
      if (!data?.rows) return []
      return Object.values(data.rows).filter(
        (row) => !hiddenIds.has(String(row?.sectionId ?? ''))
      )
    }, [data?.rows, hiddenIds])

    const handleRowAction = (e, rowId, hovering) => {
      if (activeMapAction !== 2) return
      handleTooltip(e, rowId, hovering)
      setHoveredRowId(hovering ? rowId : null)
    }

    const handleTooltip = (e, rowId, show) => {
      if (show) {
        const row = data.rows[rowId]
        const rowDetails = {
          type: 'row',
          sectionNumber: row?.sectionNumber,
          rowNumber: row?.rowNumber,
        }
        showTooltip(rowDetails, { evt: e })
      } else {
        hideTooltip()
      }
    }

    const selectSeat = (id) => {
      if (data.seats[id]) {
        selectRegularSeats(id)
      }
    }

    const seatMouseToggle = useCallback((seatId, hovering) => {
      mapStateManager.setState(seatId, { selected: hovering })
    }, [])

    const handleRowSelect = (rowId) => {
      if (activeMapAction !== 2) return

      let rowSeatIds = getSeatIdsForRow(rowId, data.rows, data.seats)

      selectRegularSeats(undefined, rowSeatIds)
    }

    const getSeatIdsForRow = (rowId, rows, seats) => {
      const targetRow = rows[rowId]
      if (!targetRow) return [] // Return empty array if the row is not found

      return targetRow.seats.filter((seatId) => {
        if (!seats[seatId]) {
          return false
        }
        return true
      })
    }

    const handleSeatMouseEnter = (e, seatId) => {
      if (activeMapAction === 2 || activeMapAction === 1) return

      const seat = data.seats[seatId]
      const customDetails = getSeatTooltipDetails?.(seatId)
      const seatDetails = {
        type: 'seat',
        sectionNumber: customDetails?.sectionNumber ?? seat?.sectionNumber,
        rowNumber: customDetails?.rowNumber ?? seat?.rowNumber,
        seatNumber: customDetails?.seatNumber ?? seat?.seatNumber,
        ...customDetails,
      }

      if (isDisabled) {
        if (
          seatDetails.sectionNumber == null &&
          seatDetails.rowNumber == null &&
          seatDetails.seatNumber == null
        ) {
          return
        }
        showTooltip(seatDetails, { evt: e })
        return
      }

      showTooltip(seatDetails, { evt: e })

      if (customDetails?.clusterKey) {
        onPurchaserClusterHover?.(customDetails.clusterKey)
      } else {
        seatMouseToggle(seatId, true)
      }
    }

    const handleSeatMouseLeave = (seatId) => {
      if (activeMapAction === 2 || activeMapAction === 1) return

      if (isDisabled) {
        hideTooltip()
        return
      }

      hideTooltip()
      const customDetails = getSeatTooltipDetails?.(seatId)
      if (customDetails?.clusterKey) {
        onPurchaserClusterHover?.(null)
      } else {
        seatMouseToggle(seatId, false)
      }
    }

    const handleSeatClick = (seatId) => {
      if (activeMapAction === 2 || activeMapAction === 1) return
      selectSeat(seatId)
    }

    return (
      <g
        className="seats"
        onMouseLeave={() => {
          mapStateManager.resetState()
          onPurchaserClusterHover?.(null)
        }}
      >
        <AccessibleIcon />
        {rows.map((row) => (
          <g key={row.rowId}>
            {row.seats.map((seatId) => {
              const seat = data.seats[seatId]
              if (!seat) return null
              return (
                <Seat
                  key={seat.seatId}
                  seat={seat}
                  getSeatState={getSeatState}
                  useFrontendSeatSizing={useFrontendSeatSizing}
                  seatScale={seatScale}
                  seatBorderRadius={seatBorderRadius}
                  onClick={handleSeatClick}
                  onMouseEnter={handleSeatMouseEnter}
                  onMouseLeave={handleSeatMouseLeave}
                />
              )
            })}
            {/* This is for selecting by row, could still delegate the events to the layer, but its fine for now */}
            {row?.path && (
              <path
                d={row.path}
                data-row-selector="true"
                pointerEvents={activeMapAction === 2 ? 'auto' : 'none'}
                onClick={() => handleRowSelect(row.rowId)}
                onMouseOver={(e) => handleRowAction(e, row.rowId, true)}
                onMouseOut={(e) => handleRowAction(e, row.rowId, false)}
                opacity={hoveredRowId === row.rowId ? 0.2 : 0}
                fill="#3E8BF7"
              />
            )}
          </g>
        ))}
      </g>
    )
  }
)

export default Seats
